import React from 'react';
import type { FC } from 'react';
import { Trans, useTranslation } from '@wix/cli-i18n';
import { isType, match } from 'variant';
import { DeploymentStatus } from '@wix/document-management-client';
import {
  Alert,
  Link,
  Spinner,
  useAsync,
  useAsyncCallback,
} from '@wix/cli-ui-kit';
import type { DeploymentSourceTypes } from '../gridapp/deployApp.js';
import { useProjectModel } from '../model.js';
import { SelectPreviewSource } from './SelectPreviewSource.js';
import { useCreatePreview } from './useCreatePreview.js';

interface PreviewCommandOptions {
  source?: DeploymentSourceTypes;
}

export const PreviewCommand: FC<PreviewCommandOptions> = (options) => {
  const { t } = useTranslation();
  const { model } = useProjectModel();
  const createPreview = useCreatePreview();

  const { status, execute: handleCreatingPreview } = useAsyncCallback(
    async (_, previewSource: DeploymentSourceTypes) => {
      const { previewUrl, deploymentStatus } =
        await createPreview(previewSource);

      if (isType(deploymentStatus, DeploymentStatus.Error)) {
        throw deploymentStatus.deploymentError;
      }

      return previewUrl;
    }
  );

  // Create preview when source is passed as an option
  useAsync(async () => {
    if (options.source) {
      void handleCreatingPreview(options.source);
    }
  }, []);

  return (
    <>
      {!options.source && (
        <SelectPreviewSource
          projectFolder={model.projectFolder}
          onSubmit={({ value }) => handleCreatingPreview(value.type)}
        />
      )}
      {match(status, {
        NotRequested: () => null,
        Error: () => null,
        Loading: () => <Spinner text={t('preview_command.creating_preview')} />,
        Success: ({ result: previewUrl }) => (
          <Alert type="success">
            <Trans
              i18nKey="preview_command.preview_created"
              components={[<Link url={previewUrl} />]}
            />
          </Alert>
        ),
      })}
    </>
  );
};
